<template>
  <div>
    <v-card v-if="loading || fetching" class="py-4 pl-8 pr-5" color="pwt_snow" elevation="3">
      <v-row no-gutters>
        <v-col md="2" class="d-flex align-center">
          <div style="height: 70px; width: 100%; overflow: hidden">
            <v-skeleton-loader type="image" />
          </div>
        </v-col>
        <v-col md="10">
          <v-skeleton-loader type="paragraph" />
        </v-col>
      </v-row>
    </v-card>
    <div class="position-relative" v-else-if="vehicle">
      <v-card
        class="pa-0 position-relative"
        :style="{
          padding: showTick ? '25px 30px 25px 25px !important' : '10px 30px 20px 25px !important',
          borderRadius: '10px',
          border: '1px solid #2eaeff',
          background: 'rgb(var(--v-theme-pwt_pastel_blue))',
          boxShadow: '0px 5px 13px 0px rgba(0, 0, 0, 0.07)',
        }"
        @click.stop="showVehicleDetails"
      >
        <v-row class="flex-column flex-md-row" no-gutters>
          <v-col md="2">
            <!-- image section -->
            <v-img
              contain
              :src="imageSrc(vehicle)"
              aspect-ratio="1"
              :width="mobile ? '40%' : '100%'"
              :height="mobile ? 'auto' : '100%'"
              style="margin: auto"
            ></v-img>
          </v-col>
          <v-col md="10">
            <!-- details section -->
            <v-card-title class="pb-0">
              {{
                vehicle.licensePlate ||
                vehicle.name ||
                vehicle.evModel?.name ||
                'Current default vehicle'
              }}
            </v-card-title>
            <v-card-subtitle class="font-weight-medium pr-12 pt-0">
              <p v-if="vehicle.evModel">
                {{ vehicle.evModel.name }}
              </p>
            </v-card-subtitle>
            <v-row no-gutters class="px-4 mt-2">
              <v-col v-if="showAvailability" cols="4" class="d-flex align-center">
                <v-icon :color="availabilityIconColour" size="small" class="mr-1">
                  {{ availabilityIcon }}
                </v-icon>
                {{ availability }}
              </v-col>
              <v-col cols="4" class="d-flex align-center">
                <v-icon color="primary" size="small" class="mr-1"> {{ fuelIcon }} </v-icon>
                {{ fuelType }}
              </v-col>
              <v-col cols="4" class="d-flex align-center">
                <v-icon color="primary" size="small" class="mr-1"> {{ capacityIcon }} </v-icon>
                {{ capacityText }}
              </v-col>
              <v-col
                v-if="showBootCapacity && bootCapacityText"
                cols="4"
                class="d-flex align-center"
              >
                <v-icon color="primary" size="small" class="mr-1">
                  mdi-package-variant-closed
                </v-icon>
                {{ bootCapacityText }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card>
      <div v-if="showTick && vehicle" class="position-absolute" style="top: -10px; right: -5px">
        <v-hover>
          <template v-slot:default="{ isHovering, props }">
            <transition name="fade">
              <div
                v-if="isHovering"
                v-bind="props"
                class="position-absolute bg-pwt_red rounded-circle pa-1 d-flex justify-center align-center"
                style="
                  border: solid 4px #ffffff;
                  height: 30px;
                  width: 30px;
                  top: 0;
                  right: 0;
                  cursor: pointer;
                "
                @click.stop="removeVehicleFromTrip"
              >
                <v-icon color="pwt_snow" size="20" class="pa-0" icon="mdi-close" />
              </div>
              <div
                v-else
                v-bind="props"
                class="position-absolute bg-pwt_azure rounded-circle pa-1 d-flex justify-center align-center"
                style="
                  border: solid 4px #ffffff;
                  height: 30px;
                  width: 30px;
                  top: 0;
                  right: 0;
                  cursor: pointer;
                "
                @click.stop="removeVehicleFromTrip"
              >
                <v-icon color="pwt_snow" size="20" class="pa-0" icon="mdi-check" />
              </div>
            </transition>
          </template>
        </v-hover>
      </div>
    </div>
    <v-card v-else elevation="3" rounded="lg" class="py-4 pl-8 pr-5 d-flex flex-column flex-md-row">
      <div class="flex-grow-1 flex-shrink-1">
        <h5 class="text-h6">Select a vehicle for your trip</h5>
        <p class="text-caption">
          You can compare different vehicles and their performance later on.
        </p>
      </div>
      <div class="d-flex justify-center align-center mt-2 mt-sm-0">
        <OutlinedBlockBtn shape="box" @click="showVehicleGalleryDialog = true">
          Add
        </OutlinedBlockBtn>
      </div>
    </v-card>
    <GenericDialog :activator="showVehicleGalleryDialog" @close="showVehicleGalleryDialog = false">
      <VehicleSearchContent @vehicle-select="handleVehicleSelect" show-generic />
    </GenericDialog>
  </div>
</template>
<script setup lang="ts">
import type Trip from '@/classes/trip_classes/trip'
import Vehicle from '@/classes/vehicle_classes/vehicle'
import { GettersTypes, MutationTypes, type State } from '@/store/store_types'
import { useDashboardNavStore } from '@/store/stores/dashboard_nav.store'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import GenericDialog from '../dialog-wrappers/GenericDialog.vue'
import VehicleSearchContent from '../trips/comparison/VehicleSearchContent.vue'
import OutlinedBlockBtn from '../ui-elements/buttons/OutlinedBlockBtn.vue'

const store = useStore<State>()
const { mobile } = useDisplay()
const fetching = computed<boolean>(() => store.getters[GettersTypes.fetching])
const trip = computed<Trip | undefined>(() => store.getters[GettersTypes.selectedTripData])
const vehicle = computed<Vehicle | undefined>(() =>
  trip.value ? trip.value?.vehicle : store.getters[GettersTypes.selectedVehicleData],
)
const showVehicleGalleryDialog = ref(false)
const {
  loading = false,
  showTick = false,
  showAvailability = false,
  showBootCapacity = false,
} = defineProps<{
  loading?: boolean
  showTick?: boolean
  showAvailability?: boolean
  showBootCapacity?: boolean
}>()

function imageSrc(vehicle: Vehicle): string {
  // get uploaded image to car in first instance.
  const images = vehicle.imageSrcPaths
  if (images?.[0]) return images[0]
  // get model image in second instance.
  if (vehicle.evModel)
    return vehicle.evModel?.imageSrc ?? getAssetSrc('car_images/placeholder_car.png')
  // get company logo image in third instance.
  const company = store.state.fleet
  if (company && company.logoSrc) {
    return company.logoSrc
  }
  // get default image in fourth instance.
  return getAssetSrc('car_images/placeholder_car.png')
}

async function handleVehicleSelect(vehicle: Vehicle): Promise<void> {
  // close dialog
  showVehicleGalleryDialog.value = false
  // update trip vehicle
  if (!trip.value) return
  trip.value.vehicle = vehicle
  await trip.value.quickPlan(true) // force quick plan
  // update global state
  store.commit(MutationTypes.updateIndividualTrip, trip.value)
}

/**
 * Resets the vehicle of the selected trip to undefined, and updates the trip in the global state.
 * This method is used to clear the vehicle selection of a trip.
 * @returns {Promise<void>}
 */
async function removeVehicleFromTrip() {
  if (!trip.value) return
  trip.value.vehicle = undefined
  await trip.value.quickPlan(true) // force quick plan
  // update global state
  store.commit(MutationTypes.updateIndividualTrip, trip.value)
}

const dashboardNavStore = useDashboardNavStore()

function showVehicleDetails() {
  if (!vehicle.value) return
  store.commit(MutationTypes.setViewedFleetVehicle, vehicle.value.localId)
  dashboardNavStore.setDashboardDialogContent('vehicle-details')
  dashboardNavStore.setShowDashboard(true)
}

const availabilityIcon = computed(() => {
  const availability: boolean | undefined = undefined // TODO: figure out how to get availability of vehicle
  switch (availability as boolean | undefined) {
    case true:
      return 'mdi-check-circle-outline'
    case false:
      return 'mdi-close-circle-outline'
    default:
      return 'mdi-minus-circle-outline'
  }
})

const availabilityIconColour = computed(() => {
  const availability: boolean | undefined = undefined // TODO: figure out how to get availability of vehicle
  switch (availability as boolean | undefined) {
    case true:
      return 'success'
    case false:
      return 'error'
    default:
      return 'primary'
  }
})

const availability = computed(() => {
  const availability: boolean | undefined = undefined // TODO: figure out how to get availability of vehicle
  switch (availability as boolean | undefined) {
    case true:
      return 'available'
    case false:
      return 'unavailable'
    default:
      return 'unknown'
  }
})

const fuelIcon = computed(() => vehicle.value?.fuelTypeIcon)

const fuelType = computed(() => vehicle.value?.fuelTypeDisplayText)

const capacityIcon = computed(() => vehicle.value?.fuelCapacityIcon)

const capacityText = computed(() => vehicle.value?.fuelCapacityDisplayText)

const bootCapacityText = computed(() => vehicle.value?.bootCapacityText)
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
  opacity: 0.6; /* Add this line to set a minimum opacity value */
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
